<template>
  <SendingGlobal v-if="sendingDocument" />
  <SentSuccess v-if="sentDocumentSuccess" />
  <PrinterGlobal v-if="printingPDF" />
  <div class="page-document">
    <div v-if="isLoading" class="has-text-centered mt-6">
      <span class="loader is-loading is-large"></span>
    </div>

    <div v-else>
      <nav class="breadcrumb" aria-label="breadcrumbs">
        <ul>
          <li><router-link to="/dashboard">Dashboard</router-link></li>
          <li>
            <router-link to="/dashboard/documents">Documents</router-link>
          </li>
          <li class="is-active">
            <router-link
              :to="{ name: 'DashboardDocuments' }"
              aria-current="true"
            >
              {{ document.number || 'Draft' }}
            </router-link>
          </li>
        </ul>
      </nav>

      <div class="columns is-multiline">
        <div class="column is-12">
          <div class="is-flex is-align-items-center mb-4" style="gap: 0.5rem">
            <!-- Previous -->
            <button
              class="button is-light is-small"
              :disabled="!navigation.previous"
              @click="goTo(navigation.previous)"
              title="Previous (←)"
            >
              ←
            </button>

            <!-- Next -->
            <button
              class="button is-light is-small"
              :disabled="!navigation.next"
              @click="goTo(navigation.next)"
              title="Next (→)"
            >
              →
            </button>

            <!-- Title -->
            <h1 class="title mb-0" style="margin-left: 0.5rem">
              Document - {{ document.number || '—' }}

              <span v-if="document.is_draft" class="tag is-warning ml-2">
                Draft
              </span>
            </h1>
          </div>

          <div class="buttons">
            <button
              v-if="document.is_draft"
              @click="finalizeDocument"
              :disabled="isFinalizing"
              class="button is-primary"
            >
              {{ isFinalizing ? 'Finalizing…' : 'Finalize' }}
            </button>

            <button
              v-if="
                !document.is_draft && !document.is_paid && !document.is_credited
              "
              @click="setAsPaid()"
              class="button is-success"
            >
              Set as paid
            </button>

            <button
              v-if="
                !document.is_draft &&
                !document.is_credited &&
                document.document_type === 'invoice'
              "
              @click="confirmCreateCreditNote"
              class="button is-danger"
            >
              Create Credit Note
            </button>

            <button v-if="canEdit" @click="goToEdit" class="button is-warning">
              Edit Document
            </button>
            <button class="button is-light" @click="copyDocument">Copy</button>
            <button
              v-if="!document.is_draft"
              class="button is-link"
              @click="getPdf"
            >
              <span class="icon"><i class="fas fa-file-pdf"></i></span>
              <span>Download PDF</span>
            </button>

            <button
              v-if="!document.is_draft"
              class="button is-primary"
              @click="isSendModalOpen = true"
            >
              Send
            </button>

            <button
              class="button is-danger"
              @click="confirmDelete"
              v-if="
                !document.is_draft && !document.is_paid && !document.is_credited
              "
            >
              Delete Document
            </button>
          </div>
        </div>

        <!-- === References (BuyerReference / OrderReference) === -->
        <div class="column is-12 mb-4">
          <div class="box">
            <h3 class="is-size-4 mb-5">References</h3>
            <div class="columns">
              <div class="column is-6">
                <p>
                  <strong>General Reference:</strong>
                  <span class="is-family-monospace">{{
                    document.sender_reference || '—'
                  }}</span>
                </p>
              </div>
              <div class="column is-6">
                <p>
                  <strong>Internal reference :</strong>
                  <span class="is-family-monospace">{{
                    document.structured_reference || '—'
                  }}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- === Third Party Info (extended) === -->
        <div class="column is-12 mb-4">
          <div class="box">
            <h3 class="is-size-4 mb-5">Third Party</h3>

            <p>
              <strong>{{ document.third_party_name }}</strong>
            </p>

            <p v-if="document.third_party_address1">
              {{ document.third_party_address1 }}
            </p>
            <p v-if="document.third_party_address2">
              {{ document.third_party_address2 }}
            </p>

            <p
              v-if="document.third_party_zipcode || document.third_party_place"
            >
              {{ document.third_party_zipcode }}
              {{ document.third_party_place }}
            </p>

            <!-- Country full name if you had it before -->
            <p v-if="document.third_party_country">
              {{ document.third_party_country }}
            </p>

            <!-- New: country code / region -->
            <p v-if="thirdPartyCountryCode">
              <strong>Country code:</strong>
              <span class="is-family-monospace">{{
                thirdPartyCountryCode
              }}</span>
            </p>
            <p v-if="thirdPartyRegion">
              <strong>Region:</strong>
              <span class="is-family-monospace">{{ thirdPartyRegion }}</span>
            </p>

            <!-- New: Peppol endpoint/scheme (only shown if provided/snapshotted on the doc) -->
            <p v-if="buyer?.peppol_endpoint_id">
              <strong>Peppol endpoint:</strong>
              <span class="is-family-monospace">{{
                buyer.peppol_endpoint_id
              }}</span>
              <span v-if="buyer.peppol_scheme_id"
                >({{ buyer.peppol_scheme_id }})</span
              >
            </p>
          </div>
        </div>

        <!-- === Items Table === -->
        <div class="column is-12 mb-4">
          <div class="box">
            <h3 class="is-size-4 mb-5">Items</h3>

            <table class="table is-fullwidth">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Quantity</th>
                  <th>VAT %</th>
                  <th>Unit Price</th>
                  <th>Net</th>
                  <th>VAT</th>
                  <th>Gross</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="item in document.document_items" :key="item.id">
                  <td>{{ getItemDisplayName(item) }}</td>
                  <td>{{ getItemDescription(item) }}</td>
                  <td>{{ item.quantity }}</td>
                  <td>{{ item.vat_rate }}%</td>
                  <td>{{ n2(item.unit_price) }} €</td>
                  <td>{{ n2(item.line_net_amount) }} €</td>
                  <td>{{ n2(item.line_vat_amount) }} €</td>
                  <td>{{ n2(item.line_gross_amount) }} €</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- === Summary === -->
        <div class="column is-12">
          <div class="box">
            <h3 class="is-size-4 mb-5">Summary</h3>

            <div class="columns">
              <div class="column is-6">
                <p><strong>Net amount:</strong> {{ document.net_amount }} €</p>
                <p><strong>VAT amount:</strong> {{ document.vat_amount }} €</p>
                <p>
                  <strong>Gross amount:</strong> {{ document.gross_amount }} €
                </p>
                <p><strong>Bank account:</strong> {{ document.bankaccount }}</p>
              </div>

              <div class="column is-6">
                <p>
                  <strong>Issue date:</strong>
                  {{
                    document.issue_date_formatted || document.issue_date || '—'
                  }}
                </p>

                <p>
                  <strong>Due date:</strong>
                  {{ document.due_date_formatted || '—' }}
                </p>
                <p><strong>Status:</strong> {{ getStatusLabel() }}</p>
                <p><strong>Document type:</strong> {{ getDocumentType() }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /columns -->
    </div>
    <!-- /v-else -->

    <!-- === Send modal === -->
    <div class="modal" :class="{ 'is-active': isSendModalOpen }">
      <div class="modal-background" @click="isSendModalOpen = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Send Document</p>
          <button class="delete" @click="isSendModalOpen = false"></button>
        </header>

        <section class="modal-card-body">
          <div class="tabs is-toggle is-fullwidth">
            <ul>
              <li :class="{ 'is-active': activeTab === 'email' }">
                <a @click.prevent="activeTab = 'email'">
                  <span class="icon"><i class="fas fa-envelope"></i></span>
                  <span>Email</span>
                </a>
              </li>

              <li :class="{ 'is-active': activeTab === 'peppol' }">
                <a @click.prevent="activeTab = 'peppol'">
                  <span class="icon"><i class="fas fa-network-wired"></i></span>
                  <span>Peppol</span>
                </a>
              </li>
            </ul>
          </div>

          <div v-if="activeTab === 'email'">
            <div class="content">
              <p>Send this document by email to the client.</p>
            </div>

            <div class="field">
              <label class="label">Recipient</label>
              <div class="control">
                <input
                  v-model="emailForm.to"
                  class="input"
                  type="email"
                  :class="{ 'is-danger': emailError }"
                  :placeholder="
                    document.third_party_email || 'client@email.com'
                  "
                />
              </div>
              <p v-if="emailError" class="help is-danger">
                Please enter a valid email
              </p>
            </div>

            <div class="field">
              <label class="label">Language</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="emailForm.language">
                    <option value="fr">French</option>
                    <option value="nl">Dutch</option>
                    <option value="en">English</option>
                  </select>
                </div>
              </div>
            </div>

            <article class="message is-info mt-4">
              <div class="message-body">
                The invoice will be sent as a PDF attachment.
              </div>
            </article>
          </div>

          <!---<div v-if="activeTab === 'peppol'">-->
          <!-- === Peppol Tab Content === -->
          <div v-if="activeTab === 'peppol'">
            <div class="content">
              <p>
                <strong>Seller endpoint:</strong>
                <span class="is-family-monospace">
                  {{ document.organization_peppol_endpoint || '—' }}
                </span>
                <span v-if="document.organization_peppol_scheme">
                  ({{ document.organization_peppol_scheme }})
                </span>
              </p>

              <p>
                <strong>Buyer endpoint:</strong>
                <span class="is-family-monospace">
                  {{ buyer?.peppol_endpoint_id || '—' }}
                </span>
                <span v-if="buyer?.peppol_scheme_id">
                  ({{ buyer.peppol_scheme_id }})
                </span>
              </p>

              <!-- === Peppol readiness warning === -->
              <!-- Case 1 : Already sent -->
              <article
                class="message is-info"
                v-if="document && document.peppol_status === 'sent'"
              >
                <div class="message-body">
                  📤 This document has already been sent via Peppol.
                </div>
              </article>

              <!-- Case 2 : Not sent yet -->
              <article
                class="message"
                :class="peppolReady ? 'is-success' : 'is-warning'"
                v-else-if="buyer"
              >
                <div class="message-body">
                  <template v-if="peppolReady">
                    ✅ Ready to export via Peppol.
                  </template>

                  <template v-else>
                    ⚠️ Buyer electronic address (<strong>EndpointID</strong>) or
                    <strong>Scheme</strong> is missing.<br />
                    This document cannot be sent via Peppol until the buyer
                    information is completed.
                    <br />

                    <router-link
                      v-if="document && document.third_party"
                      :to="{
                        name: 'EditThirdParty',
                        params: { id: document.third_party },
                      }"
                      class="has-text-weight-semibold"
                    >
                      Fix now
                    </router-link>
                  </template>
                </div>
              </article>
            </div>
          </div>
        </section>

        <footer class="modal-card-foot">
          <button
            v-if="activeTab === 'email'"
            class="button is-primary"
            @click="sendEmail"
          >
            Send Email
          </button>

          <button
            v-if="activeTab === 'peppol'"
            class="button"
            :class="alreadySentPeppol ? 'is-warning' : 'is-primary'"
            :disabled="!peppolReady"
            @click="sendPeppol"
          >
            {{ alreadySentPeppol ? 'Resend via Peppol' : 'Send via Peppol' }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/utils/axios';
import fileDownload from 'js-file-download';
import { toast } from 'bulma-toast';
import SendingGlobal from '@/components/SendingGlobal.vue';
import SentSuccess from '@/components/SentSuccess.vue';
import PrinterGlobal from '@/components/PrinterGlobal.vue';

export default {
  name: 'DetailDocument',
  components: {
    SendingGlobal,
    SentSuccess,
    PrinterGlobal,
  },
  data() {
    return {
      document: {},
      buyer: null,
      isLoading: false,
      isFinalizing: false,
      isSendModalOpen: false,
      sendingDocument: false,
      sentDocumentSuccess: false,
      printingPDF: false,
      activeTab: 'email', // 'email' or 'peppol'
      emailForm: { to: '', subject: '', body: 'Please find invoice attached' },
      emailError: false,
      navigation: {
        previous: null,
        next: null,
      },
    };
  },
  computed: {
    peppolReady() {
      const tp = this.buyer;
      // align with ThirdParty model fields
      const hasEndpoint = !!tp?.peppol_endpoint_id;
      const hasScheme = !!tp?.peppol_scheme_id;
      return hasEndpoint && hasScheme;
    },
    canEdit() {
      // Allow editing invoices (and drafts if you want)
      return this.document && !this.document.is_paid;
    },
    alreadySentPeppol() {
      // Optional: if you track Peppol sending status, you can disable the button or show a warning
      return this.document.peppol_status === 'sent';
    },

    /* Safe getters for third-party extended fields (use fallbacks for robustness) */
    thirdPartyCountryCode() {
      // Prefer explicit code, fallback to previous 'country' if you had it
      return (
        this.document.third_party_country_code ||
        this.document.third_party_country ||
        null
      );
    },
    thirdPartyRegion() {
      return this.document.third_party_region || null;
    },
    thirdPartyPeppolEndpoint() {
      return this.document.third_party_peppol_endpoint || null;
    },
    thirdPartyPeppolScheme() {
      return this.document.third_party_peppol_scheme || null;
    },
  },
  methods: {
    goToEdit() {
      this.$router.push({
        name: 'EditDocument',
        params: { id: this.document.id },
      });
    },
    handleKeyNavigation(e) {
      // Avoid triggering when typing
      const tag = document.activeElement.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (e.key === 'ArrowLeft' && this.navigation.previous) {
        this.goTo(this.navigation.previous);
      }

      if (e.key === 'ArrowRight' && this.navigation.next) {
        this.goTo(this.navigation.next);
      }
    },
    goTo(id) {
      // Safety check
      if (!id) return;
      this.$router.push({
        name: 'DetailDocument',
        params: { id },
      });
    },

    async fetchDocument(id) {
      this.isLoading = true;
      try {
        const { data } = await api.get(`/documents/${id}/`);
        this.document = data;
        if (data.third_party) {
          const tpRes = await api.get(`/third-parties/${data.third_party}/`);
          this.buyer = tpRes.data;
        }
        await this.fetchNavigation(); // refresh navigation data when document changes
      } finally {
        this.isLoading = false;
      }
    },
    async fetchNavigation() {
      if (!this.document.id) return;
      try {
        const res = await api.get(`/documents/${this.document.id}/navigation/`);
        this.navigation = res.data;
      } catch (error) {
        console.error('Failed to fetch navigation data:', error);
      }
    },

    getPdf() {
      const documentID = this.$route.params.id;
      this.printingPDF = true;
      api
        .get(`/documents/${documentID}/generate_pdf/`, {
          responseType: 'blob',
          skipLoader: true,
        })
        .then((response) => {
          // Use number when available, otherwise fallback to id
          const base = this.document.number
            ? `document_${this.document.number}`
            : `document_${this.document.id}`;
          fileDownload(response.data, `${base}.pdf`);
        })
        .catch((error) => {
          console.error(JSON.stringify(error));
        })
        .finally(() => {
          this.printingPDF = false;
        });
    },

    getStatusLabel() {
      // Draft if no number yet
      if (!this.document.number) return 'Draft';
      return this.document.is_paid ? 'Paid' : 'Not paid';
    },

    getDocumentType() {
      // Normalize label
      return this.document.document_type === 'credit_note'
        ? 'Credit Note'
        : 'Invoice';
    },

    n2(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n.toFixed(2) : '0.00';
    },

    getItemDisplayName(item) {
      return item.item ? item.item.name : item.title;
    },
    getItemDescription(item) {
      return item.item ? item.item.description : item.description;
    },

    async setAsPaid() {
      const documentID = this.document.id;
      try {
        await api.patch(`/documents/${documentID}/`, { is_paid: true });
        toast({
          message: 'The document has been marked as paid',
          type: 'is-success',
          dismissible: true,
          duration: 2000,
          position: 'bottom-right',
        });
        this.document.is_paid = true;
      } catch (error) {
        console.error(JSON.stringify(error));
      }
    },

    async confirmCreateCreditNote() {
      let message =
        'Are you sure you want to create a credit note for this invoice?';
      if (!this.document.is_paid) {
        message += '\n\n⚠️ Warning: This invoice has not been marked as paid.';
      }
      const confirmed = window.confirm(message);
      if (confirmed) await this.startCreditNote();
    },

    async confirmDelete() {
      const confirmed = window.confirm(
        'Are you sure you want to delete this document?'
      );
      if (!confirmed) return;
      try {
        await api.delete(`/documents/${this.document.id}/`);
        toast({
          message: 'Document successfully deleted.',
          type: 'is-success',
          duration: 2000,
          dismissible: true,
        });
        this.$router.push('/dashboard/documents');
      } catch (error) {
        console.error('Failed to delete document:', error);
        toast({
          message: 'Failed to delete the document.',
          type: 'is-danger',
          duration: 2000,
          dismissible: true,
        });
      }
    },

    async finalizeDocument() {
      try {
        this.isFinalizing = true;
        await api.post(`/documents/${this.document.id}/finalize/`);
        await this.fetchDocument(this.document.id); // refresh
        toast({
          message: 'Document finalized',
          type: 'is-success',
          dismissible: true,
          duration: 2000,
          position: 'bottom-right',
        });
      } catch (error) {
        console.error('Finalize failed:', error);
        toast({
          message: error?.response?.data?.detail || 'Finalize failed',
          type: 'is-danger',
          dismissible: true,
          duration: 2500,
          position: 'bottom-right',
        });
      } finally {
        this.isFinalizing = false;
      }
    },
    async copyDocument() {
      try {
        const res = await api.get(`/documents/${this.document.id}/`);

        const doc = res.data;

        // Remove fields you DON'T want
        delete doc.id;
        delete doc.number;
        delete doc.created_at;
        delete doc.updated_at;

        // Store in localStorage (simple solution)
        localStorage.setItem('document_copy', JSON.stringify(doc));

        // Redirect to create page
        this.$router.push({ name: 'AddDocument' });
      } catch (err) {
        console.error(err);
      }
    },

    async startCreditNote() {
      try {
        const { data } = await api.get(
          `/documents/${this.document.id}/prepare-credit-note/`
        );
        this.$router.push({
          name: 'AddDocument',
          query: { preset: JSON.stringify(data) },
        });
      } catch (e) {
        console.error(e);
        toast({ message: 'Failed to prepare credit note', type: 'is-danger' });
      }
    },

    async sendEmail() {
      const recipient = this.emailForm.to || this.document.third_party_email;

      // ❌ No email
      if (!recipient) {
        this.emailError = true;

        toast({
          message: 'Please enter a recipient email address',
          type: 'is-danger',
        });

        return;
      }

      this.emailError = false;

      try {
        // 🔥 START LOADING
        this.sendingDocument = true;

        toast({
          message: 'Sending email…',
          type: 'is-info',
        });

        const res = await api.post(
          `/documents/${this.document.id}/send-email/`,
          {
            to: this.emailForm.to,
            language: this.emailForm.language,
          },
          {
            skipLoader: true,
          }
        );

        // 🔥 SUCCESS STATE
        if (res.data.success) {
          this.sentDocumentSuccess = true;

          toast({
            message: '✅ Email sent successfully',
            type: 'is-success',
          });
        } else {
          toast({
            message: '⚠️ Email sending failed',
            type: 'is-warning',
          });
        }
      } catch (err) {
        console.error(err);

        toast({
          message: '🚨 Send failed',
          type: 'is-danger',
        });
      } finally {
        // 🔥 STOP LOADING
        this.sendingDocument = false;

        // 🔥 SUCCESS ANIMATION (same as Peppol)
        if (this.sentDocumentSuccess) {
          setTimeout(async () => {
            this.sentDocumentSuccess = false;
            this.isSendModalOpen = false;

            // refresh document
            await this.fetchDocument(this.document.id);
          }, 2000);
        }
      }
    },

    async sendPeppol() {
      try {
        this.sendingDocument = true;

        toast({
          message: 'Sending document via Peppol…',
          type: 'is-info',
        });

        const res = await api.post(
          '/peppol/send-document/',
          {
            document_id: this.document.id,
          },
          {
            skipLoader: true,
          }
        );

        if (res.data.success) {
          this.sendPeppol = false;
          this.sentDocumentSuccess = true;
          toast({
            message: '✅ Document successfully sent via Peppol',
            type: 'is-success',
          });
        } else {
          toast({
            message: '⚠️ Recommand returned an error',
            type: 'is-warning',
          });
        }
      } catch (err) {
        console.error('Peppol error:', err.response?.data || err);

        toast({
          message: '🚨 Peppol send failed',
          type: 'is-danger',
        });
      } finally {
        this.sendingDocument = false;
        if (this.sentDocumentSuccess) {
          setTimeout(async () => {
            this.sentDocumentSuccess = false;
            this.isSendModalOpen = false;
            await this.fetchDocument(this.document.id);
          }, 2000);
        }
      }
    },
  },

  mounted() {
    this.fetchDocument(this.$route.params.id);
    window.addEventListener('keydown', this.handleKeyNavigation);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyNavigation);
  },

  watch: {
    '$route.params.id': {
      immediate: true,
      handler(newId) {
        this.fetchDocument(newId);
      },
    },
  },
};
</script>

<style scoped>
.badge-warning {
  padding: 0.25rem 0.5rem;
  border-radius: 0.5rem;
  background: #f59e0b;
  color: #111;
  margin-left: 0.5rem;
}

/* loader unchanged */
.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #00d1b2;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  display: inline-block;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
